import { useEffect, useRef, type ReactNode } from 'react';

interface ResponsiveFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  header: ReactNode;
  footer: ReactNode;
  children: ReactNode;
  zIndex?: number;
  overlayContent?: ReactNode;
  /** Disable backdrop click to close */
  disableBackdropClose?: boolean;
}

export default function ResponsiveFormModal({
  isOpen,
  onClose,
  header,
  footer,
  children,
  zIndex = 60,
  overlayContent,
  disableBackdropClose = true,
}: ResponsiveFormModalProps) {
  const scrollPosRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      scrollPosRef.current = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosRef.current}px`;
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPosRef.current);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[var(--modal-z)] flex items-center justify-center lg:items-start lg:pt-[8vh]" style={{ '--modal-z': zIndex } as React.CSSProperties}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 hidden lg:block"
        onClick={disableBackdropClose ? undefined : onClose}
      />
      {/* Mobile backdrop */}
      <div className="absolute inset-0 bg-black/40 lg:hidden" />

      {/* Modal Container */}
      <div className="relative bg-background-50 flex flex-col overflow-hidden w-full h-full lg:h-auto lg:max-h-[88vh] lg:rounded-2xl lg:border lg:border-background-200 lg:shadow-2xl lg:max-w-xl lg:mx-4"
        style={{
          height: '100dvh',
        }}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 bg-background-50 border-b border-background-100 z-10"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          {header}
        </div>

        {/* Scrollable Content */}
        <div
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          {children}
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 bg-background-50 border-t border-background-100 z-10"
          style={{
            boxShadow: '0 -1px 8px rgba(0,0,0,0.04)',
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          }}>
          {footer}
        </div>

        {/* Overlay content (draft prompt, exit confirm, etc.) */}
        {overlayContent && (
          <div className="absolute inset-0 z-20 bg-background-50/90 backdrop-blur-sm flex items-center justify-center">
            {overlayContent}
          </div>
        )}
      </div>
    </div>
  );
}